import { useEffect } from "react";
import { useForm } from "react-hook-form";

import { Button } from "../../shared/ui/button.js";
import { ResourceState } from "../../shared/ui/resource-state.js";
import { useSaveSettings, useSettings } from "./queries.js";

interface PreferenceFields {
  readonly confirmLiveOrders: boolean;
  readonly dateFormat: "iso" | "locale";
  readonly timeZone: string;
}

export function PreferencesForm() {
  const settings = useSettings();
  const save = useSaveSettings();
  const { register, handleSubmit, reset } = useForm<PreferenceFields>();
  useEffect(() => {
    if (settings.data !== undefined) reset(settings.data);
  }, [reset, settings.data]);

  if (settings.isPending) {
    return (
      <ResourceState
        kind="loading"
        title="Loading preferences"
        description="TradingApp is loading your applicable workspace settings."
      />
    );
  }
  if (settings.isError) {
    return (
      <ResourceState
        kind="error"
        title="Preferences unavailable"
        description="Your existing settings were not changed. Try loading them again."
        action={<Button onClick={() => void settings.refetch()}>Try again</Button>}
      />
    );
  }

  const submit = handleSubmit(async (values) => {
    await save.mutateAsync(values);
  });
  return (
    <form className="content-card form-stack" onSubmit={(event) => void submit(event)}>
      <div>
        <p className="eyebrow">Applicable preferences</p>
        <h2>Trading workspace</h2>
        <p className="section-description">
          Option, template, Bot Group, training, and model settings are intentionally absent.
        </p>
      </div>
      <label className="check-field">
        <input type="checkbox" {...register("confirmLiveOrders")} />
        <span>
          <strong>Confirm live orders</strong>
          <small>Require an explicit confirmation before enabling live execution.</small>
        </span>
      </label>
      <div className="field-grid">
        <div className="field-stack">
          <label htmlFor="date-format">Date format</label>
          <select id="date-format" {...register("dateFormat")}>
            <option value="locale">Local format</option>
            <option value="iso">ISO 8601</option>
          </select>
        </div>
        <div className="field-stack">
          <label htmlFor="time-zone">Time zone</label>
          <input id="time-zone" required {...register("timeZone", { required: true })} />
        </div>
      </div>
      {save.isError ? (
        <p className="form-message form-message--error">
          Preferences could not be saved. Try again.
        </p>
      ) : null}
      {save.isSuccess ? (
        <p className="form-message form-message--success">Preferences saved.</p>
      ) : null}
      <Button disabled={save.isPending} type="submit">
        {save.isPending ? "Saving…" : "Save preferences"}
      </Button>
    </form>
  );
}
